import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { TicketListItem } from '@/api/ticketsApi'
import { Skeleton } from '@/components/common/States'
import { SLA_TONE_CLASS, slaCell } from '@/lib/slaDisplay'
import { priorityDotClass, shortName, statusLabel, statusPillClass, ticketSubline } from '@/lib/ticketLabels'
import { cn } from '@/lib/utils'

// Fixed lanes from artboard 01 (px): dot 28, ID 110, subject grows, customer 150,
// product 130, status 130, assignee 90, SLA 90.
const COL = {
  dot: 'w-7 shrink-0',
  id: 'w-[110px] shrink-0',
  subject: 'min-w-0 grow',
  customer: 'w-[150px] shrink-0',
  product: 'w-[130px] shrink-0',
  status: 'w-[130px] shrink-0',
  assignee: 'w-[90px] shrink-0',
  sla: 'w-[90px] shrink-0 text-right',
}

// The CGT table header: brand blue, white labels.
const headCell = 'text-label font-semibold text-table-header-foreground'

export function QueueTableHeader({ showCustomer }: { showCustomer: boolean }) {
  const { t } = useTranslation()
  return (
    <div role="row" className="flex items-center bg-table-header px-3 py-2.5">
      <div className={COL.dot} />
      <div role="columnheader" className={cn(COL.id, headCell)}>{t('queue.columns.id')}</div>
      <div role="columnheader" className={cn(COL.subject, headCell)}>{t('queue.columns.subject')}</div>
      {showCustomer ? <div role="columnheader" className={cn(COL.customer, headCell)}>{t('queue.columns.customer')}</div> : null}
      <div role="columnheader" className={cn(COL.product, headCell)}>{t('queue.columns.product')}</div>
      <div role="columnheader" className={cn(COL.status, headCell)}>{t('queue.columns.status')}</div>
      <div role="columnheader" className={cn(COL.assignee, headCell)}>{t('queue.columns.assignee')}</div>
      <div role="columnheader" className={cn(COL.sla, headCell)}>{t('queue.columns.sla')}</div>
    </div>
  )
}

export function QueueRow({ ticket, now, showCustomer }: { ticket: TicketListItem; now: number; showCustomer: boolean }) {
  const { t } = useTranslation()
  const sla = slaCell(ticket.sla, ticket.status, now)
  return (
    <Link
      role="row"
      to={`/tickets/${ticket.key}`}
      className="flex items-center border-b border-line px-3 py-3.5 transition-colors last:border-b-0 hover:bg-accent/50 focus-visible:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
    >
      <div className={cn(COL.dot, 'flex')}>
        <span className={cn('size-2 shrink-0 rounded-sm', priorityDotClass(ticket.priority))} aria-label={ticket.priority} />
      </div>
      <div role="cell" className={cn(COL.id, 'font-mono text-meta text-ink-body')}>{ticket.key}</div>
      <div role="cell" className={cn(COL.subject, 'flex flex-col gap-0.5 pr-4')}>
        <span className="truncate text-body font-medium text-ink-strong">{ticket.subject}</span>
        <span className="truncate text-label text-ink-muted">
          {ticketSubline(ticket.type, ticket.priority, ticket.component?.name ?? ticket.linkedRecord?.key)}
        </span>
      </div>
      {showCustomer ? (
        <div role="cell" className={cn(COL.customer, 'flex flex-col gap-0.5 pr-2')}>
          <span className="truncate text-meta text-ink">{ticket.customer.name}</span>
          {ticket.tenant ? <span className="truncate text-label text-ink-muted">· {ticket.tenant.name}</span> : null}
        </div>
      ) : null}
      <div role="cell" className={cn(COL.product, 'truncate pr-2 text-meta text-ink-body')}>{ticket.product.name}</div>
      <div role="cell" className={cn(COL.status, 'flex')}>
        <span className={cn('inline-block truncate rounded-sm px-2 py-[3px] text-label font-medium', statusPillClass(ticket.status))}>
          {statusLabel(ticket.status)}
        </span>
      </div>
      <div role="cell" className={cn(COL.assignee, 'truncate text-meta text-ink-body')}>
        {ticket.assignee ? shortName(ticket.assignee.name) : <span className="text-ink-placeholder">{t('queue.unassigned')}</span>}
      </div>
      <div
        role="cell"
        className={cn(COL.sla, 'font-mono text-meta', SLA_TONE_CLASS[sla.tone])}
        title={
          sla.paused
            ? sla.text
              ? t('queue.slaPausedLeft', { time: sla.text })
              : t('queue.slaPaused')
            : ticket.sla.breached
              ? t('queue.slaBreached')
              : undefined
        }
      >
        {sla.paused ? (
          // Paused: the time left at pause, never a countdown (or "overdue").
          <span className="flex flex-col items-end leading-tight">
            <span className="font-sans text-label text-ink-muted">{t('queue.slaPausedLabel')}</span>
            {sla.text ? <span>{sla.text}</span> : null}
          </span>
        ) : (
          sla.text
        )}
      </div>
    </Link>
  )
}

/** Rows shaped like the real ones (dot, ID, subject over its subline, status pill, SLA), so nothing jumps when they arrive. */
export function QueueSkeleton({ rows = 6 }: { rows?: number }) {
  const { t } = useTranslation()
  // The grey rows say nothing to a screen reader; the status label does.
  return (
    <div role="status" aria-busy="true" aria-label={t('common.loading')}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center border-b border-line px-3 py-3.5 last:border-b-0">
          <div className={COL.dot}>
            <Skeleton className="size-2" />
          </div>
          <Skeleton className="mr-6 h-3 w-[72px]" />
          <div className="flex grow flex-col gap-1.5 pr-4">
            <Skeleton className="h-3 w-3/5" />
            <Skeleton className="h-2.5 w-2/5" />
          </div>
          <Skeleton className="mr-10 h-5 w-[88px] rounded-sm" />
          <Skeleton className="h-3 w-[48px]" />
        </div>
      ))}
    </div>
  )
}
